import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import BottomNav from "../components/BottomNav";
import TrendCard from "../components/TrendCard";
import Avatar from "../components/Avatar";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import { createDuel, getTrendIndexes } from "../lib/api";
import { Sparkles, Crown, Shield, X, Info, Swords, Copy, Share2, MessageCircle } from "lucide-react";

const CATS = ["all", "bollywood", "reality", "creator", "music", "athletes"];

export default function DuelCreate() {
  const nav = useNavigate();
  const [indexes, setIndexes] = useState([]);
  const [cat, setCat] = useState("all");
  const [picks, setPicks] = useState([]);
  const [captain, setCaptain] = useState(null);
  const [vc, setVc] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(null);

  useEffect(() => { getTrendIndexes().then(setIndexes); }, []);

  const filtered = useMemo(
    () => (cat === "all" ? indexes : indexes.filter(x => x.category === cat)),
    [indexes, cat]
  );

  const pickedSet = new Set(picks);
  const picksDetail = picks.map((pid) => indexes.find((x) => x.index_id === pid)).filter(Boolean);
  const projectedPoints = picksDetail.reduce((a, x) => {
    const base = Number(x.form_points ?? x.heat_score ?? 0);
    const mult = x.index_id === captain ? 2 : x.index_id === vc ? 1.5 : 1;
    return a + base * mult;
  }, 0);

  const togglePick = (idx) => {
    if (pickedSet.has(idx.index_id)) {
      setPicks(picks.filter((p) => p !== idx.index_id));
      if (captain === idx.index_id) setCaptain(null);
      if (vc === idx.index_id) setVc(null);
    } else {
      if (picks.length >= 6) { toast("Lineup full — remove to swap."); return; }
      setPicks([...picks, idx.index_id]);
    }
  };

  const submit = async () => {
    if (picks.length !== 6) return toast.error("Pick exactly 6 stars.");
    if (!captain || !vc) return toast.error("Crown a Captain and Vice-Captain.");
    if (captain === vc) return toast.error("Captain and VC must differ.");
    setSubmitting(true);
    try {
      const res = await createDuel({
        contest_id: "duel",
        picks,
        captain_id: captain,
        vice_captain_id: vc,
      });
      confetti({ particleCount: 140, spread: 90, origin: { y: 0.4 }, colors: ["#FF0055", "#00F0FF", "#B026FF"] });
      setCreated(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create duel.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen pb-safe" data-testid="duel-create-page">
      <Header title="Duel a Friend" />
      <div className="max-w-xl mx-auto px-4 pt-4 space-y-4">
        <div className="glass rounded-2xl p-4 relative overflow-hidden">
          <div aria-hidden className="pointer-events-none absolute -top-10 -right-10 w-40 h-40 rounded-full opacity-40" style={{ background: "radial-gradient(circle,#FF0055,transparent 70%)" }} />
          <div className="flex items-center gap-2">
            <Swords className="w-5 h-5 text-[#FF0055]" />
            <p className="text-xs uppercase tracking-widest font-bold text-white/60">1v1 Friend Challenge</p>
          </div>
          <h1 className="font-display text-2xl font-bold mt-1">Build. Challenge. Flex.</h1>
          <div className="mt-3 rounded-xl p-3 flex items-start gap-2" style={{ background: "#FF005510", border: "1px solid #FF005533" }}>
            <Info className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#FF0055" }} />
            <p className="text-xs text-white/75 leading-relaxed">
              Pick 6 stars, crown a Captain (2×) and VC (1.5×). We'll generate a shareable invite link. Whoever's lineup scores higher <b className="text-white">wins +500 XP</b>. Creator earns +75 XP just for starting one.
            </p>
          </div>
        </div>

        {/* Picks tray */}
        <div className="glass-strong rounded-2xl p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#00F0FF]" />
              <p className="font-display font-semibold">Your Squad ({picks.length}/6)</p>
            </div>
            <span className="text-xs font-mono-brand" style={{ color: projectedPoints > 0 ? "#FFD600" : "rgba(255,255,255,0.5)" }}>⚡ {projectedPoints.toFixed(0)} proj. pts</span>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {Array.from({ length: 6 }).map((_, i) => {
              const idx = picksDetail[i];
              if (!idx) return (
                <div key={i} className="aspect-square rounded-xl border border-dashed border-white/15 grid place-items-center text-white/30 text-xs">
                  Slot {i + 1}
                </div>
              );
              const isC = captain === idx.index_id;
              const isV = vc === idx.index_id;
              return (
                <div key={idx.index_id} className={`relative rounded-xl overflow-hidden ring-1 ${isC ? "ring-[#B026FF]" : isV ? "ring-[#00F0FF]" : "ring-white/10"}`}>
                  <div className="w-full aspect-square">
                    <Avatar name={idx.name} category={idx.category} symbol={idx.symbol} size={140} rounded={0} className="!w-full !h-full !ring-0" />
                  </div>
                  <div className="absolute inset-0" style={{ background: "linear-gradient(180deg,transparent 40%, rgba(0,0,0,0.85))" }} />
                  <button
                    onClick={() => togglePick(idx)}
                    className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 grid place-items-center"
                    data-testid={`duel-remove-${idx.handle}`}
                    aria-label="remove"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                  <div className="absolute bottom-1 left-1 right-1">
                    <p className="text-[10px] font-bold truncate">{idx.name}</p>
                    <div className="flex gap-1 mt-1">
                      <button
                        onClick={() => setCaptain(captain === idx.index_id ? null : idx.index_id)}
                        data-testid={`duel-c-${idx.handle}`}
                        className={`text-[9px] px-1.5 py-0.5 rounded ${isC ? "bg-[#B026FF] text-black font-bold" : "bg-white/10"}`}
                      >C 2x</button>
                      <button
                        onClick={() => setVc(vc === idx.index_id ? null : idx.index_id)}
                        data-testid={`duel-vc-${idx.handle}`}
                        className={`text-[9px] px-1.5 py-0.5 rounded ${isV ? "bg-[#00F0FF] text-black font-bold" : "bg-white/10"}`}
                      >VC 1.5x</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex gap-2 mt-3 text-[11px] text-white/50">
            <span className="inline-flex items-center gap-1"><Crown className="w-3 h-3 text-[#B026FF]" /> Captain 2×.</span>
            <span className="inline-flex items-center gap-1"><Shield className="w-3 h-3 text-[#00F0FF]" /> VC 1.5×.</span>
          </div>
        </div>

        <div className="flex gap-2 overflow-x-auto py-1">
          {CATS.map((c) => (
            <button
              key={c}
              data-testid={`duel-filter-${c}`}
              onClick={() => setCat(c)}
              className="chip whitespace-nowrap px-3 py-1.5 text-xs"
              style={cat === c
                ? { background: "linear-gradient(90deg,#FF0055,#B026FF)", color: "#000", border: "1px solid transparent" }
                : { background: "rgba(255,255,255,0.05)", color: "#fff", border: "1px solid rgba(255,255,255,0.1)" }}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="space-y-3">
          {filtered.slice(0, 60).map((idx) => (
            <TrendCard
              key={idx.index_id}
              index={idx}
              selected={pickedSet.has(idx.index_id)}
              captain={captain === idx.index_id}
              vc={vc === idx.index_id}
              onClick={() => togglePick(idx)}
            />
          ))}
        </div>

        <button
          onClick={submit}
          disabled={submitting || picks.length !== 6 || !captain || !vc}
          data-testid="duel-create-submit"
          className="sticky bottom-24 w-full py-3.5 rounded-full font-bold text-black disabled:opacity-40"
          style={{ background: "linear-gradient(90deg,#FF0055,#B026FF)", boxShadow: "0 0 22px rgba(255,0,85,0.35)" }}
        >
          {submitting ? "Locking squad…" : "Lock Squad & Get Invite Link"}
        </button>

        <div className="h-10" />
      </div>
      <BottomNav />
      {created && (
        <DuelInviteModal
          duel={created}
          onClose={() => nav(`/duel/${created.invite_code}`)}
        />
      )}
    </div>
  );
}

function DuelInviteModal({ duel, onClose }) {
  const shareUrl = `${window.location.origin}/duel/${duel.invite_code}`;
  const msg = `I just challenged you on Fanpuff 🔥 Beat my lineup (${duel.creator_lineup.points.toFixed(0)} pts) — pick your squad here: ${shareUrl}`;

  const copy = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Link copied"); }
    catch { toast.error("Could not copy"); }
  };
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "Fanpuff Duel", text: msg, url: shareUrl }); }
      catch {}
    } else { copy(); }
  };
  const whatsapp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4" data-testid="duel-invite-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      <div className="relative w-full max-w-md glass-strong rounded-3xl p-6 border border-white/10">
        <div className="flex items-center gap-2">
          <Swords className="w-5 h-5 text-[#FF0055]" />
          <p className="text-xs uppercase tracking-widest font-bold text-white/60">Duel Created</p>
        </div>
        <h3 className="font-display text-2xl font-bold mt-2">Share the invite</h3>
        <p className="text-sm text-white/70 mt-1">
          Your lineup scored <b className="text-white">{duel.creator_lineup.points.toFixed(0)} pts</b>. First friend to beat it wins.
        </p>

        <div className="mt-4 rounded-2xl p-4 text-center" style={{ background: "linear-gradient(135deg,#FF005522,#B026FF22)", border: "1px solid #FF005555" }}>
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/50 font-bold">Invite code</p>
          <p className="font-display text-4xl font-bold mt-1 tracking-widest" data-testid="duel-invite-code">{duel.invite_code}</p>
          <p className="text-[11px] text-white/50 mt-2 break-all">{shareUrl}</p>
        </div>

        <div className="mt-4 grid grid-cols-3 gap-2">
          <button onClick={copy} data-testid="duel-copy-btn" className="glass rounded-xl py-2.5 inline-flex items-center justify-center gap-1 text-xs font-bold hover:bg-white/10">
            <Copy className="w-4 h-4" /> Copy
          </button>
          <button onClick={whatsapp} data-testid="duel-whatsapp-btn" className="rounded-xl py-2.5 inline-flex items-center justify-center gap-1 text-xs font-bold text-black" style={{ background: "#00FF66" }}>
            <MessageCircle className="w-4 h-4" /> WhatsApp
          </button>
          <button onClick={share} data-testid="duel-share-btn" className="rounded-xl py-2.5 inline-flex items-center justify-center gap-1 text-xs font-bold text-black" style={{ background: "linear-gradient(90deg,#FF0055,#B026FF)" }}>
            <Share2 className="w-4 h-4" /> Share
          </button>
        </div>

        <button
          onClick={onClose}
          data-testid="duel-view-after-create"
          className="mt-4 w-full py-3 rounded-full glass hover:bg-white/10 text-sm font-bold"
        >
          View duel status →
        </button>
      </div>
    </div>
  );
}
